import { ChangeEvent, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { InputDatePicker } from "../../../components/InputDatePicker";
import { ImprovementPlanActionFormDto } from "../types";
import { SelectItemDto } from "../../../types/SelectItemDto";
import { ActionStatusEnum } from "../types/enums";
import AppCommentsList, { AppCommentsListItem } from "../../../components/AppCommentsList";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { convertFileToBase64 } from "../../../utils/formatters/FileFormatter";

type ImprovementPlanActionFormProps = {
    actionForm: ImprovementPlanActionFormDto;
    errors: { [key: string]: string | undefined };
    updateField: (field: keyof ImprovementPlanActionFormDto, value: ImprovementPlanActionFormDto[keyof ImprovementPlanActionFormDto]) => void;
    responsibleOptions?: SelectItemDto[];
};

export default function ImprovementPlanActionForm({
    actionForm,
    errors,
    updateField,
    responsibleOptions = []
}: ImprovementPlanActionFormProps) {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const improvementPlanForm = useSelector((state: RootState) => state.improvementPlanForm.data);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const parseNumberOrUndefined = (value: string) => (value === "" ? undefined : Number(value));

    const handleAddComment = () => {
        const comments = actionForm.comments ?? [];
        updateField("comments", [...comments, { id: 0, comment: "" }]);
    };

    const handleUpdateComment = (index: number, item: AppCommentsListItem) => {
        const comments = [...(actionForm.comments ?? [])];
        comments[index] = { ...comments[index], comment: item.value };
        updateField("comments", comments);
    };

    const handleRemoveComment = (index: number) => {
        const comments = (actionForm.comments ?? []).filter((_, currentIndex) => currentIndex !== index);
        updateField("comments", comments);
    };

    const handleOpenFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleAddFiles = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files ?? []);
        if (selectedFiles.length === 0) {
            return;
        }

        const filesToAdd = await Promise.all(selectedFiles.map(async (file) => ({
            id: 0,
            name: file.name,
            type: file.type || "application/octet-stream",
            content: await convertFileToBase64(file),
        })));

        updateField("files", [...(actionForm.files ?? []), ...filesToAdd]);
        event.target.value = "";
    };

    const handleRemoveFile = (index: number) => {
        const files = (actionForm.files ?? []).filter((_, currentIndex) => currentIndex !== index);
        updateField("files", files);
    };

    return (
        <Form>
            <Row className="mt-1">
                <Col xs={12}>
                    <h5 className="mb-0">{actionForm.id == 0 ? t("improvementPlan:actionForm.newTitle") : t("improvementPlan:actionForm.editTitle")}</h5>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:name")}:</Form.Label>
                        <Form.Control
                            type="text"
                            value={improvementPlanForm.title}
                            disabled
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.status")} * :</Form.Label>
                        <Form.Select
                            value={actionForm.status ?? ""}
                            isInvalid={!!errors.status}
                            onChange={(e) => updateField("status", parseNumberOrUndefined(e.target.value))}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {(Object.values(ActionStatusEnum)
                                .filter((option) => typeof option === "number") as number[])
                                .map((value) => (
                                    <option key={value} value={value}>
                                        {t(`improvementPlan:actionForm.status.${ActionStatusEnum[value]}`)}
                                    </option>
                                )
                            )}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.status}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-1">
                <Col xs={12}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.description")} :</Form.Label>
                        <Form.Control
                            type="text"
                            value={actionForm.description}
                            isInvalid={!!errors.description}
                            onChange={(e) => updateField("description", e.target.value)}
                        />
                    </Form.Group>
                    <Form.Control.Feedback type="invalid">
                        {errors.description}
                    </Form.Control.Feedback>
                </Col>
            </Row>

            <Row className="mt-1">
                <Col xs={12} md={6}>
                    <Form.Group>
                        <Form.Label>{t("improvementPlan:actionForm.fields.responsible")} :</Form.Label>
                        <Form.Select
                            value={actionForm.responsibleId ?? ""}
                            isInvalid={!!errors.responsibleId}
                            onChange={(e) => updateField("responsibleId", parseNumberOrUndefined(e.target.value))}
                        >
                            <option value="">{t("common:selectDefaultText")}</option>
                            {responsibleOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.responsibleId}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={12} md={6}>
                    <Form.Group>
                        <InputDatePicker
                            value={actionForm.startDate ? new Date(actionForm.startDate) : null}
                            onChange={(date: Date | null) => updateField("startDate", date ?? undefined)}
                            label={`${t("common:form.fields.startDate")} :`}
                            hasError={!!errors.startDate}
                            errorMessage={errors.startDate}
                        />
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mt-1">
                <Col xs={12} md={6}>
                    <Form.Group>
                        <InputDatePicker
                            value={actionForm.expectedEndDate ? new Date(actionForm.expectedEndDate) : null}
                            onChange={(date: Date | null) => updateField("expectedEndDate", date ?? undefined)}
                            label={`${t("improvementPlan:actionForm.fields.expectedEndDate")} :`}
                            hasError={!!errors.expectedEndDate}
                            errorMessage={errors.expectedEndDate}
                        />
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group>
                        <InputDatePicker
                            value={actionForm.endDate ? new Date(actionForm.endDate) : null}
                            onChange={(date: Date | null) => updateField("endDate", date ?? undefined)}
                            label={`${t("common:form.fields.endDate")} :`}
                            hasError={!!errors.endDate}
                            errorMessage={errors.endDate}
                        />
                    </Form.Group>
                </Col>
            </Row>
            
            <Row className="mt-4">
                <Col xs={12} md={8}>
                    <h5 className="mb-0">{t("improvementPlan:actionForm.fields.documents")}</h5>
                </Col>
                <Col xs={12} md={4} className="mt-2 mt-md-0 d-flex justify-content-md-end">
                    <Button type="button" variant="secondary" onClick={handleOpenFilePicker}>
                        {t("common:form.addDocument")}
                    </Button>
                    <Form.Control
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        multiple
                        onChange={handleAddFiles}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col xs={12}>
                    <div className="d-flex flex-wrap gap-2">
                        {(actionForm.files ?? []).map((file, index) => (
                            <div
                                key={`action-file-${file.id}-${index}`}
                                className="border rounded px-2 py-1 d-flex align-items-center gap-2"
                            >
                                <i className="bi bi-file-earmark-pdf"></i>
                                <span>{file.name}</span>
                                <Button
                                    type="button"
                                    variant="link"
                                    className="p-0 text-danger text-decoration-none"
                                    onClick={() => handleRemoveFile(index)}
                                    aria-label={t("common:buttons.buttonDelete")}
                                >
                                    <i className="bi bi-x-lg"></i>
                                </Button>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
            <AppCommentsList
                label={t("improvementPlan:actionForm.fields.comments")}
                placeholder={t("improvementPlan:actionForm.fields.comments")}
                items={(actionForm.comments ?? []).map((comment) => ({ id: comment.id, value: comment.comment }))}
                getItemError={(index) => errors[`comments[${index}].comment`] as string | undefined}
                onAdd={handleAddComment}
                onRemove={handleRemoveComment}
                onChange={handleUpdateComment}
                addAriaLabel={t("improvementPlan:actionForm.addComment")}
                removeAriaLabel={t("common:buttons.buttonDelete")}
            />
        </Form>
    );
}
